import { Box, Stack, Tag, Text } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { format } from 'date-fns';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { DraftListRow } from '../../hooks/requestDraft/useDraftList';

type Props = {
  row: DraftListRow;
  isSelected: boolean;
  onSelect: () => void;
  /** Set on the row that should pull the next page in; every other row passes nothing. */
  onInView?: () => void;
};

/** "LAX/SFO": one side of the lane, or null when it names no airport. */
const joinSide = (codes: (string | null)[] = []): string | null => codes.filter(Boolean).join('/') || null;

/**
 * The matched request's lane. A booking confirmation's payload names no lane of its own, so a draft not yet matched to
 * a request shows none.
 */
const laneFor = (row: DraftListRow): string | null => {
  const origin = joinSide(row.request?.origin_airports.map((airport) => airport.airport_as_origin_id));
  const destination = joinSide(row.request?.destination_airports.map((airport) => airport.airport_as_destination_id));
  if (!origin && !destination) return null;
  return `${origin ?? '?'} → ${destination ?? '?'}`;
};

export const DraftListItem = ({ row, isSelected, onSelect, onInView }: Props) => {
  const lane = laneFor(row);
  const receivedAt = format(new Date(row.created_at), 'MMM d, HH:mm');
  const accountName = row.account?.name;
  const background = isSelected ? 'blackAlpha.100' : undefined;
  const hoverBackground = isSelected ? 'blackAlpha.100' : 'blackAlpha.50';
  const accentColor = isSelected ? 'primary.500' : 'transparent';

  const showLane = !!lane;
  const showAccount = !!accountName;

  const [ref, isInView] = useInView();

  useEffect(() => {
    if (isInView) onInView?.();
  }, [isInView, onInView]);

  return (
    <Box
      ref={ref}
      as="button"
      textAlign="left"
      w="full"
      px="4"
      py="3"
      borderBottomWidth="1px"
      bg={background}
      borderLeftWidth="3px"
      borderLeftColor={accentColor}
      _hover={{ bg: hoverBackground }}
      onClick={onSelect}
    >
      <Stack spacing="1">
        <Stack direction="row" align="center" spacing="2">
          <Show if={showLane}>
            <Text fontSize="xs" fontWeight="bold" color="gray.600">
              {lane}
            </Text>
          </Show>
          <Box flex="1" />
          <Text fontSize="xs" color="gray.500">
            {receivedAt}
          </Text>
        </Stack>
        <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
          {row.email_subject}
        </Text>
        <Stack direction="row" align="center" spacing="2">
          <Text fontSize="xs" color="gray.600" noOfLines={1} flex="1">
            {row.email_from}
          </Text>
          <Show if={showAccount}>
            <Text fontSize="xs" color="gray.500" noOfLines={1}>
              {accountName}
            </Text>
          </Show>
          {/* Confidence only surfaces when it is NEWS: the model's self-reported
              number saturates at 0.95 on everything unambiguous, so printing it
              on every row is noise. The exceptional state is the tag. */}
          <Show if={row.is_low_confidence}>
            <Tag size="sm" colorScheme="orange">
              Low confidence
            </Tag>
          </Show>
        </Stack>
      </Stack>
    </Box>
  );
};
